import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * StandardizedChip Component
 *
 * A standardized chip component with fixed dimensions.
 * All chips use the same size regardless of text length.
 * Shows full text in tooltip on hover when text is truncated.
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Chip label text
 * @param {string} props.color - MUI color name (default, primary, secondary, error, info, success, warning)
 * @param {string} props.variant - Chip variant ('filled' or 'outlined')
 * @param {ReactNode} props.icon - Optional icon to display
 * @param {Object} props.sx - Additional sx styles to merge
 * @param {number} props.width - Fixed width (default: 90)
 * @param {number} props.height - Fixed height (default: 24)
 * @param {string} props.tooltipTitle - Custom tooltip title (defaults to label if not provided)
 */
const StandardizedChip = ({
  label,
  color = 'default',
  variant = 'filled',
  icon,
  sx = {},
  width = 90,
  height = 24,
  tooltipTitle,
  ...otherProps
}) => {
  const chip = (
    <Chip
      label={label}
      color={color}
      variant={variant}
      icon={icon}
      size="small"
      sx={{
        width,
        height,
        fontSize: '0.75rem',
        fontWeight: 500,
        display: 'flex',
        justifyContent: 'center',
        '& .MuiChip-label': {
          px: 1.5,
          py: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          textAlign: 'center',
        },
        '& .MuiChip-icon': {
          width: 16,
          height: 16,
          marginLeft: 0.5,
          marginRight: 0,
        },
        ...sx,
      }}
      {...otherProps}
    />
  );

  // Show tooltip with full text on hover
  const tooltipText = tooltipTitle || label;

  return (
    <Tooltip title={tooltipText} arrow enterDelay={300}>
      {chip}
    </Tooltip>
  );
};

StandardizedChip.propTypes = {
  label: PropTypes.string.isRequired,
  color: PropTypes.oneOf([
    'default',
    'primary',
    'secondary',
    'error',
    'info',
    'success',
    'warning',
  ]),
  variant: PropTypes.oneOf(['filled', 'outlined']),
  icon: PropTypes.node,
  sx: PropTypes.object,
  width: PropTypes.number,
  height: PropTypes.number,
  tooltipTitle: PropTypes.string,
};

export default StandardizedChip;
